import React, { useState, useMemo } from "react";
import { Head, router } from "@inertiajs/react";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Pie,
  PieChart,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Bar,
  BarChart,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Domain {
  id: number;
  name: string;
  enabled: boolean;
}

interface AnalyticsData {
  daily_trend: Array<{
    date: string;
    count: number;
    inbox: number;
    spam: number;
    promotions: number;
    other: number;
  }>;
  distribution: Array<{ name: string; value: number; fill: string }>;
}

interface SubjectLineAnalytic {
  subject: string;
  date: string;
  domain: string;
  total_count: number;
  inbox_count: number;
  spam_count: number;
  inbox_percentage: number;
  spam_percentage: number;
  received_at: string;
}

interface Props {
  domains: Domain[];
  analyticsData: Record<string, AnalyticsData>;
  subjectLineAnalytics?: SubjectLineAnalytic[];
  selectedDateFilter?: string;
  selectedDomain?: string;
}

export default function Index({
  domains,
  analyticsData,
  subjectLineAnalytics = [],
  selectedDateFilter,
  selectedDomain: initialSelectedDomain,
}: Props) {
  const [selectedDomain, setSelectedDomain] = useState<string>(
    initialSelectedDomain || ""
  );
  const [dateFilter, setDateFilter] = useState(
    selectedDateFilter || "last_7_days"
  );

  // Subject line filters
  const [subjectDateFilter, setSubjectDateFilter] = useState(
    selectedDateFilter || "last_7_days"
  );
  const [subjectDomainFilter, setSubjectDomainFilter] = useState("");
  const [subjectSearchTerm, setSubjectSearchTerm] = useState("");

  // Chart configurations
  const barChartConfig = {
    inbox: {
      label: "Inbox",
      color: "#10b981",
    },
    spam: {
      label: "Spam",
      color: "#ef4444",
    },
    promotions: {
      label: "Promotions",
      color: "#f59e0b",
    },
    other: {
      label: "Other",
      color: "#6b7280",
    },
  } satisfies ChartConfig;

  const pieChartConfig = {
    inbox: {
      label: "Inbox",
      color: "hsl(var(--chart-1))",
    },
    spam: {
      label: "Spam",
      color: "hsl(var(--chart-2))",
    },
    promotions: {
      label: "Promotions",
      color: "hsl(var(--chart-3))",
    },
    other: {
      label: "Other",
      color: "hsl(var(--chart-4))",
    },
  } satisfies ChartConfig;

  const handleDateFilterChange = (value: string) => {
    setDateFilter(value);
    router.get(
      "/analytics",
      {
        date_filter: value,
        domain: selectedDomain,
      },
      {
        preserveState: true,
        preserveScroll: true,
      }
    );
  };

  const handleDomainFilterChange = (value: string) => {
    const domainValue = value === "all" ? "" : value;
    setSelectedDomain(domainValue);
    router.get(
      "/analytics",
      {
        date_filter: dateFilter,
        domain: domainValue,
      },
      {
        preserveState: true,
        preserveScroll: true,
      }
    );
  };

  const filteredData =
    selectedDomain && selectedDomain !== ""
      ? { [selectedDomain]: analyticsData[selectedDomain] }
      : analyticsData;

  // Filter subject line analytics
  const filteredSubjectLineAnalytics = useMemo(() => {
    return subjectLineAnalytics.filter((analytic) => {
      // Domain filter
      if (subjectDomainFilter && analytic.domain !== subjectDomainFilter) {
        return false;
      }

      // Search filter
      if (
        subjectSearchTerm &&
        !analytic.subject
          .toLowerCase()
          .includes(subjectSearchTerm.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [subjectLineAnalytics, subjectDomainFilter, subjectSearchTerm]);

  return (
    <>
      <Head title="Analytics" />

      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              Email Analytics
            </h1>
            <p className="mt-2 text-gray-600">
              Track your email delivery performance and trends
            </p>
          </div>

          <Tabs defaultValue="domain-analytics" className="w-full">
            <div className="flex justify-start mb-1">
              <TabsList>
                <TabsTrigger value="domain-analytics">
                  Domain Analytics
                </TabsTrigger>
                <TabsTrigger value="subject-performance">
                  Subject Line Performance
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="domain-analytics" className="mt-6">
              {/* Filters Section */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                <div className="flex flex-wrap gap-3 items-center">
                  <Select
                    value={dateFilter}
                    onValueChange={handleDateFilterChange}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select time range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                      <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select
                    value={selectedDomain || "all"}
                    onValueChange={handleDomainFilterChange}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select domain" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Domains</SelectItem>
                      {domains.map((domain) => (
                        <SelectItem key={domain.id} value={domain.name}>
                          {domain.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {(dateFilter !== "last_7_days" || selectedDomain) && (
                    <button
                      onClick={() => {
                        setDateFilter("last_7_days");
                        setSelectedDomain("");
                        router.get(
                          "/analytics",
                          {
                            date_filter: "last_7_days",
                            domain: "",
                          },
                          {
                            preserveState: true,
                            preserveScroll: true,
                          }
                        );
                      }}
                      className="inline-flex items-center gap-2 px-3 h-9 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors cursor-pointer"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      Clear filters
                    </button>
                  )}
                </div>
              </div>

              {Object.keys(filteredData).length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No analytics data yet
                    </h3>
                    <p className="text-gray-500">
                      Start tracking domains to see analytics data here.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-12">
                  {Object.entries(filteredData).map(
                    ([domainName, data], index) => (
                      <div key={domainName} className="space-y-6">
                        {index > 0 && (
                          <div className="border-t border-gray-200 pt-12" />
                        )}
                        <h2 className="text-2xl font-semibold text-gray-900">
                          {domainName}
                        </h2>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Daily Trend Chart */}
                          <Card>
                            <CardHeader>
                              <CardTitle>Daily Email Distribution</CardTitle>
                              <CardDescription>
                                Email folder distribution per day{" "}
                                {dateFilter === "today"
                                  ? "(Today)"
                                  : dateFilter === "last_7_days"
                                  ? "(Last 7 days)"
                                  : dateFilter === "last_30_days"
                                  ? "(Last 30 days)"
                                  : "(Last 90 days)"}
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              <ChartContainer
                                config={barChartConfig}
                                className="h-[300px]"
                              >
                                <BarChart
                                  data={data.daily_trend}
                                  margin={{
                                    top: 20,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                  }}
                                  isAnimationActive={false}
                                >
                                  <CartesianGrid
                                    vertical={false}
                                    strokeDasharray="3 3"
                                  />
                                  <XAxis
                                    dataKey="date"
                                    tickLine={false}
                                    tickMargin={10}
                                    axisLine={false}
                                    tick={{ fontSize: 12 }}
                                    interval="preserveStartEnd"
                                  />
                                  <YAxis
                                    tick={{ fontSize: 12 }}
                                    allowDecimals={false}
                                    domain={[
                                      0,
                                      (dataMax: number) =>
                                        Math.ceil(dataMax * 1.2),
                                    ]}
                                  />
                                  <ChartTooltip
                                    content={<ChartTooltipContent hideLabel />}
                                  />
                                  <ChartLegend
                                    content={<ChartLegendContent />}
                                  />
                                  <Bar
                                    dataKey="inbox"
                                    stackId="a"
                                    fill="#10b981"
                                    radius={[0, 0, 0, 0]}
                                    isAnimationActive={false}
                                  />
                                  <Bar
                                    dataKey="spam"
                                    stackId="a"
                                    fill="#ef4444"
                                    radius={[0, 0, 0, 0]}
                                    isAnimationActive={false}
                                  />
                                  <Bar
                                    dataKey="promotions"
                                    stackId="a"
                                    fill="#f59e0b"
                                    radius={[0, 0, 0, 0]}
                                    isAnimationActive={false}
                                  />
                                  <Bar
                                    dataKey="other"
                                    stackId="a"
                                    fill="#6b7280"
                                    radius={[4, 4, 0, 0]}
                                    isAnimationActive={false}
                                  >
                                    <LabelList
                                      dataKey="count"
                                      position="top"
                                      style={{
                                        fill: "#374151",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                      }}
                                      formatter={(value: number) =>
                                        value > 0 ? value : ""
                                      }
                                    />
                                  </Bar>
                                </BarChart>
                              </ChartContainer>
                            </CardContent>
                          </Card>

                          {/* Distribution Pie Chart */}
                          <Card>
                            <CardHeader>
                              <CardTitle>Inbox/Spam Distribution</CardTitle>
                              <CardDescription>
                                Where your emails are landing
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              {data.distribution.length > 0 ? (
                                <ChartContainer
                                  config={pieChartConfig}
                                  className="h-[300px]"
                                >
                                  <PieChart>
                                    <ChartTooltip
                                      content={
                                        <ChartTooltipContent hideLabel />
                                      }
                                    />
                                    <Pie
                                      data={data.distribution}
                                      cx="50%"
                                      cy="50%"
                                      labelLine={false}
                                      outerRadius={100}
                                      fill="#8884d8"
                                      dataKey="value"
                                      nameKey="name"
                                      label={renderCustomizedLabel}
                                      isAnimationActive={false}
                                    >
                                      {data.distribution.map((entry, index) => (
                                        <Cell
                                          key={`cell-${index}`}
                                          fill={entry.fill}
                                        />
                                      ))}
                                    </Pie>
                                    <ChartLegend
                                      verticalAlign="bottom"
                                      height={36}
                                      content={
                                        <div className="flex justify-center gap-4 pt-3">
                                          {data.distribution.map((entry) => (
                                            <div
                                              key={entry.name}
                                              className="flex items-center gap-1.5"
                                            >
                                              <div
                                                className="h-2 w-2 shrink-0 rounded-[2px]"
                                                style={{
                                                  backgroundColor: entry.fill,
                                                }}
                                              />
                                              <span className="text-sm font-medium">
                                                {entry.name}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      }
                                    />
                                  </PieChart>
                                </ChartContainer>
                              ) : (
                                <div className="text-center py-12 text-gray-500">
                                  No email data available for distribution
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </div>

                        {/* Summary Statistics */}
                        <Card>
                          <CardHeader>
                            <CardTitle>Summary Statistics</CardTitle>
                            <CardDescription>
                              Email distribution breakdown for {domainName}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-green-700">
                                    Inbox
                                  </span>
                                  <svg
                                    className="w-5 h-5 text-green-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                                    />
                                  </svg>
                                </div>
                                <p className="text-3xl font-bold text-green-700">
                                  {data.distribution.find(
                                    (d) => d.name === "Inbox"
                                  )?.value || 0}
                                </p>
                                <p className="text-xs text-green-600 mt-1">
                                  {data.distribution.reduce(
                                    (sum, item) => sum + item.value,
                                    0
                                  ) > 0
                                    ? `${Math.round(
                                        ((data.distribution.find(
                                          (d) => d.name === "Inbox"
                                        )?.value || 0) /
                                          data.distribution.reduce(
                                            (sum, item) => sum + item.value,
                                            0
                                          )) *
                                          100
                                      )}%`
                                    : "0%"}{" "}
                                  of total
                                </p>
                              </div>
                              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-red-700">
                                    Spam
                                  </span>
                                  <svg
                                    className="w-5 h-5 text-red-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                  </svg>
                                </div>
                                <p className="text-3xl font-bold text-red-700">
                                  {data.distribution.find(
                                    (d) => d.name === "Spam"
                                  )?.value || 0}
                                </p>
                                <p className="text-xs text-red-600 mt-1">
                                  {data.distribution.reduce(
                                    (sum, item) => sum + item.value,
                                    0
                                  ) > 0
                                    ? `${Math.round(
                                        ((data.distribution.find(
                                          (d) => d.name === "Spam"
                                        )?.value || 0) /
                                          data.distribution.reduce(
                                            (sum, item) => sum + item.value,
                                            0
                                          )) *
                                          100
                                      )}%`
                                    : "0%"}{" "}
                                  of total
                                </p>
                              </div>
                              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-yellow-700">
                                    Promotions
                                  </span>
                                  <svg
                                    className="w-5 h-5 text-yellow-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                    />
                                  </svg>
                                </div>
                                <p className="text-3xl font-bold text-yellow-700">
                                  {data.distribution.find(
                                    (d) => d.name === "Promotions"
                                  )?.value || 0}
                                </p>
                                <p className="text-xs text-yellow-600 mt-1">
                                  {data.distribution.reduce(
                                    (sum, item) => sum + item.value,
                                    0
                                  ) > 0
                                    ? `${Math.round(
                                        ((data.distribution.find(
                                          (d) => d.name === "Promotions"
                                        )?.value || 0) /
                                          data.distribution.reduce(
                                            (sum, item) => sum + item.value,
                                            0
                                          )) *
                                          100
                                      )}%`
                                    : "0%"}{" "}
                                  of total
                                </p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-gray-700">
                                    Other
                                  </span>
                                  <svg
                                    className="w-5 h-5 text-gray-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                                    />
                                  </svg>
                                </div>
                                <p className="text-3xl font-bold text-gray-700">
                                  {data.distribution.find(
                                    (d) => d.name === "Other"
                                  )?.value || 0}
                                </p>
                                <p className="text-xs text-gray-600 mt-1">
                                  {data.distribution.reduce(
                                    (sum, item) => sum + item.value,
                                    0
                                  ) > 0
                                    ? `${Math.round(
                                        ((data.distribution.find(
                                          (d) => d.name === "Other"
                                        )?.value || 0) /
                                          data.distribution.reduce(
                                            (sum, item) => sum + item.value,
                                            0
                                          )) *
                                          100
                                      )}%`
                                    : "0%"}{" "}
                                  of total
                                </p>
                              </div>
                            </div>
                            <div className="mt-6 pt-6 border-t border-gray-200">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm text-gray-500">
                                    Total Emails Tracked
                                  </p>
                                  <p className="text-2xl font-bold text-gray-900">
                                    {data.distribution.reduce(
                                      (sum, item) => sum + item.value,
                                      0
                                    )}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm text-gray-500">
                                    Inbox Rate
                                  </p>
                                  <p className="text-2xl font-bold text-green-600">
                                    {data.distribution.reduce(
                                      (sum, item) => sum + item.value,
                                      0
                                    ) > 0
                                      ? `${Math.round(
                                          ((data.distribution.find(
                                            (d) => d.name === "Inbox"
                                          )?.value || 0) /
                                            data.distribution.reduce(
                                              (sum, item) => sum + item.value,
                                              0
                                            )) *
                                            100
                                        )}%`
                                      : "0%"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="subject-performance" className="mt-6">
              {/* Subject Line Filters */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                <div className="flex flex-wrap gap-3 items-center">
                  <Select
                    value={subjectDateFilter}
                    onValueChange={(value) => {
                      setSubjectDateFilter(value);
                      router.get(
                        "/analytics",
                        { date_filter: value },
                        { preserveState: true, preserveScroll: true }
                      );
                    }}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select time range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                      <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select
                    value={subjectDomainFilter || "all"}
                    onValueChange={(value) => {
                      setSubjectDomainFilter(value === "all" ? "" : value);
                    }}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select domain" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Domains</SelectItem>
                      {[
                        ...new Set(subjectLineAnalytics.map((a) => a.domain)),
                      ].map((domain) => (
                        <SelectItem key={domain} value={domain}>
                          {domain}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <input
                    type="text"
                    placeholder="Search subject lines..."
                    value={subjectSearchTerm}
                    onChange={(e) => setSubjectSearchTerm(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />

                  {(subjectDateFilter !== "last_7_days" ||
                    subjectDomainFilter ||
                    subjectSearchTerm) && (
                    <button
                      onClick={() => {
                        setSubjectDateFilter("last_7_days");
                        setSubjectDomainFilter("");
                        setSubjectSearchTerm("");
                        router.get(
                          "/analytics",
                          { date_filter: "last_7_days" },
                          { preserveState: true, preserveScroll: true }
                        );
                      }}
                      className="inline-flex items-center gap-2 px-3 h-9 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors cursor-pointer"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      Clear filters
                    </button>
                  )}
                </div>
              </div>

              {filteredSubjectLineAnalytics.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No subject line data yet
                    </h3>
                    <p className="text-gray-500">
                      Start tracking domains to see subject line performance
                      data here.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Subject Line
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Sending Domain
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Emails
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Inbox %
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Spam %
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredSubjectLineAnalytics.map((analytic, index) => (
                          <tr
                            key={`${analytic.subject}-${analytic.date}-${index}`}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 whitespace-normal text-sm text-gray-900 max-w-xs">
                              {analytic.subject}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {analytic.domain}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(analytic.date).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                }
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                              {analytic.total_count}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {analytic.inbox_percentage}%
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                {analytic.spam_percentage}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
}

// Custom label renderer for pie chart to prevent overlapping
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
}: any) => {
  if (percent < 0.05) return null; // Don't show label for small slices

  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      className="text-sm font-medium"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};
